import { useMemo, useState, type CSSProperties } from 'react'
import type { ListModel } from '../GroceryList'
import { activeSections } from '../../lib/items'
import { Hero, SegmentBar } from './Hero'
import { splitSegments } from '../../lib/segments'
import { RestockChips } from './RestockChips'
import { SwipeRow } from './SwipeRow'
import { QuickAdd } from './QuickAdd'
import { UndoToast } from './UndoToast'
import { SidebarSlot } from '../shell/AppShell'

// Desktop and iPad List tab: the phone's list, wider (ux/02 Desktop). In the
// frame's sidebar (shell/AppShell) the hero and the category filter; one
// centred column with the add field, Running low and the aisles that have
// items, grouped like the phone's. No basket, no check circles, no swipe right.

type Filter = 'all' | string

export function PlanView({ model: m, undoRef }: { model: ListModel; undoRef: React.Ref<HTMLButtonElement> }) {
  const [filter, setFilter] = useState<Filter>('all')
  const all = useMemo(() => activeSections(m.items, m.categories, true), [m.items, m.categories])
  const selected = filter === 'all' ? null : m.categories.find(c => c.id === filter) ?? null
  // A filter whose category was deleted falls back to everything.
  const nonEmpty = all.filter(s => s.items.length > 0)
  // Every aisle with items; a picked one even when it is empty, to say so.
  const sections = selected ? all.filter(s => s.category?.id === selected.id) : nonEmpty
  // The tour's "row" step points at the first aisle with something in it.
  const firstFilled = sections.find(s => s.items.length)?.category?.id
  const split = splitSegments(all.map(s => ({ tint: m.tintFor(s.category?.id ?? null), count: s.items.length })))

  return (
    <div className="ml-plan">
      <SidebarSlot>
        <Hero count={m.active.length} label={m.active.length === 1 ? 'Item on the list' : 'Items on the list'} layout="desktop">
          <SegmentBar segments={split} label="The list by category" />
          <span className="ml-label">{nonEmpty.length} {nonEmpty.length === 1 ? 'category' : 'categories'}</span>
        </Hero>
        {m.planCaption && <button type="button" className="ml-plan-caption" onClick={() => m.onUpgrade()}>{m.planCaption}</button>}

        <nav className="ml-nav" aria-labelledby="ml-nav-label">
          <h2 id="ml-nav-label" className="ml-label ml-section-label">Categories</h2>
          <NavRow label="All items" count={m.active.length} pressed={!selected} onClick={() => setFilter('all')} />
          {all.filter(s => s.category).map(s => (
            <NavRow key={s.category!.id} label={s.category!.name} count={s.items.length}
              tint={m.tintFor(s.category!.id)} pressed={selected?.id === s.category!.id}
              onClick={() => setFilter(s.category!.id)} />
          ))}
        </nav>

      </SidebarSlot>

      <div className="ml-plan__main">
        <header className="ml-plan__header">
          <h1 className="ml-plan__title ml-display">{selected?.name ?? 'All items'}</h1>
        </header>

        <QuickAdd
          items={m.items}
          catalog={m.catalog}
          memory={m.categoryMemory}
          categories={m.categories}
          layout="desktop"
          tintFor={m.tintFor}
          onAdd={m.onAdd}
          onPutBack={m.onPutBack}
          onEditItem={(item, intent) => m.onEdit(item.id, intent)}
          plus={m.plus}
          itemsLeft={m.itemsLeft}
          unsaved={m.unsaved}
          onDiscardUnsaved={m.onDiscardUnsaved}
          onUpgrade={m.onUpgrade}
          onAddFromBefore={m.onAddFromBefore}
          onOpenFromBefore={m.onOpenFromBefore}
          onBrowse={m.onBrowse}
        />

        <RestockChips chips={m.chips} tintFor={m.tintFor} layout="desktop" onAdd={m.onChip} />

        {!m.isLoading && m.active.length === 0 && !selected && (
          <p className="ml-label ml-plan__empty">Nothing planned yet</p>
        )}

        {sections.map(({ category, items }) => (
          <section key={category?.id ?? 'other'} className="ml-section"
            style={{ '--ml-tint': m.tintFor(category?.id ?? null) } as CSSProperties}>
            <h2 className="ml-section__head">
              <span className="ml-dot" aria-hidden="true" />
              <span className="ml-label ml-section__label">{category?.name ?? 'Other'}</span>
              <span className="ml-section__count ml-num">{items.length}</span>
            </h2>
            {items.length === 0 && <p className="ml-caption ml-plan__none">Nothing in {category?.name ?? 'Other'}</p>}
            <ul className="ml-rows" data-tour={items.length > 0 && category?.id === firstFilled ? 'list-row' : undefined}>
              {items.map(item => (
                <SwipeRow key={item.id} item={item} mode="plan"
                  tint={m.tintFor(item.category_id)}
                  visual={m.swipe.visual(item.id)}
                  bind={m.swipe.bind(item.id)}
                  onEdit={m.onEdit}
                  onDelete={id => m.onDelete(id)} />
              ))}
            </ul>
          </section>
        ))}

        <UndoToast ref={undoRef} pending={m.undo.pending} layout="desktop" onUndo={m.undo.undo} />
      </div>
    </div>
  )
}

function NavRow({ label, count, tint, pressed, onClick }: {
  label: string; count: number; tint?: string; pressed: boolean; onClick: () => void
}) {
  return (
    <button type="button" className={`ml-nav__row${pressed ? ' is-selected' : ''}`} aria-pressed={pressed} onClick={onClick}
      style={tint ? ({ '--ml-tint': tint } as CSSProperties) : undefined}>
      <span className={tint ? 'ml-dot' : 'ml-dot ml-dot--all'} aria-hidden="true" />
      <span className="ml-nav__label">{label}</span>
      <span className="ml-nav__count ml-num">{count}</span>
    </button>
  )
}
